'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

/**
 * Whether the enclosing overlay (`Dialog` / `Sheet`) is busy: a submit taken
 * in it is in flight (ruled 2026-10-18: a seated task does not vanish
 * mid-submit). An overlay is busy when its caller passes `busy`, or when a
 * component inside its body reports its own in-flight state through
 * `useOverlayBusy`, the way `OverlayFoot` reaches the overlay's foot. While
 * busy, the overlay's close paths close nothing: the Dialog's X wears the
 * `IconButton`'s pending look, and Esc, the backdrop and the Sheet's swipe are
 * inert. The busy control that took the act shows the wait.
 *
 * The context holds the overlay's report function; `null` = not inside an
 * overlay, where the hook does nothing.
 */
const OverlayBusyContext = createContext<(() => () => void) | null>(null);

export const OverlayBusyProvider = OverlayBusyContext.Provider;

/**
 * Report a submit's in-flight state to the overlay around this component:
 * pass the same flag the submit button's `pending` reads. One line in a form
 * that is seated in a `Dialog` or a `Sheet`; outside an overlay it does
 * nothing, so a form that renders on a page as well needs no branch. Reported
 * in a layout effect, so the overlay is busy before the next paint.
 */
export function useOverlayBusy(pending: boolean): void {
  const report = useContext(OverlayBusyContext);
  useLayoutEffect(() => {
    if (!report || !pending) return;
    return report();
  }, [report, pending]);
}

/**
 * The overlay's own half (Dialog and Sheet only): whether it is busy, from its
 * `busy` prop or a reporting child, and the report function its
 * `OverlayBusyProvider` publishes. While open and busy it also cancels the Esc
 * keydown itself: preventing the dialog's `cancel` is not enough, because a
 * second Esc with no user activation between the two is a close request the
 * browser does not let the page cancel, and Chromium closed the native dialog
 * under a still-open `open` prop (measured 2026-10-18). Captured on the
 * document, because the focus can sit outside the dialog once its submit is
 * disabled.
 */
export function useOverlayBusyState(
  open: boolean,
  busyProp: boolean,
): { busy: boolean; report: () => () => void } {
  const [reported, setReported] = useState(0);
  const report = useCallback(() => {
    setReported((n) => n + 1);
    return () => setReported((n) => n - 1);
  }, []);
  const busy = busyProp || reported > 0;
  useEffect(() => {
    if (!open || !busy) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, busy]);
  return { busy, report };
}
